import type { FC, ReactNode } from 'react'
import type { Article } from '@/types'

import Link from 'next/link'
import { Clock, Eye } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber, readTime } from '@/lib/format'
import { Avatar } from '@/components/ds/avatar'
import { cardStyles } from '@/components/ds/card'
import { CategoryBadge, TypeBadge } from '@/components/knowledge/badges'
import { PERSON_BY_SLUG } from '@/constants/people'

interface ArticleCardProps {
    article: Article
    action?: ReactNode
    className?: string
}

export const ArticleCard: FC<ArticleCardProps> = ({ article, action, className }): ReactNode => {
    const author = PERSON_BY_SLUG[article.author]

    return (
        <article className={cardStyles(true, cn('group relative flex h-full flex-col gap-3 p-5', className))}>
            <div className='flex flex-wrap items-center gap-2'>
                <CategoryBadge category={article.category} />
                <TypeBadge type={article.type} />
                {action && <div className='relative z-10 ms-auto'>{action}</div>}
            </div>

            <h3 className='text-body font-bold leading-7 text-navy transition-colors group-hover:text-primary-deep'>
                <Link
                    href={`/knowledge/${article.slug}`}
                    className='after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-primary/45'
                >
                    {article.title}
                </Link>
            </h3>

            <p className='line-clamp-2 text-small text-muted-foreground'>
                {article.summary}
            </p>

            <div className='mt-auto flex items-center justify-between gap-3 border-t border-border pt-3 text-caption text-muted-foreground'>
                {author && (
                    <span className='inline-flex min-w-0 items-center gap-2'>
                        <Avatar name={author.name} tone={author.tone} size='xs' />
                        <span className='truncate font-medium text-foreground'>{author.name}</span>
                    </span>
                )}

                <span className='inline-flex shrink-0 items-center gap-3'>
                    <span className='inline-flex items-center gap-1'>
                        <Clock className='size-3.5 text-subtle' aria-hidden='true' />
                        {readTime(article.readMinutes)}
                    </span>

                    <span className='inline-flex items-center gap-1'>
                        <Eye className='size-3.5 text-subtle' aria-hidden='true' />
                        {formatNumber(article.views)}
                    </span>
                </span>
            </div>
        </article>
    )
}
